import isDescopeMfaEnabled from '~/composables/use-descope-mfa-flag'
import useDescopeSession from '~/composables/use-descope-session'
import useForceMfaFlag from '~/composables/use-force-mfa-flag'
import useMfaPostLoginGate from '~/composables/use-mfa-post-login-gate'

/*
 * Where a brand-new account goes straight after a registration form: the full-page
 * Authentication form and the Descope web register form. Modal registration enrols inline
 * (startMfaSetup) and never comes here. This is the MFA hand-off the onboarding page used
 * to run before CLUB-912 removed that step.
 *
 * Registration enrolment is mandatory whatever force_MFA says — setup-mfa.vue never offers
 * skip to a session that isn't parked. force_MFA only decides whether the session is also
 * parked, i.e. a real lockout rather than an offer:
 *
 *  - true:  suspendForEnrollment before handing off, so changing the URL to /dashboard/
 *           finds no token and mh-middleware bounces it to login. `destination` is resumed
 *           once enrolment completes.
 *  - false: the session stays live and `destination` travels as setup-mfa's `?next=`.
 *
 * force_MFA and the contact details are read before suspending: suspendAuthSession()
 * (inside suspendForEnrollment) wipes userStore.userProfile.
 *
 * MFA off, or no Descope session to authorize the enrolment calls (e.g. a Member Hub
 * registration while feature_DescopeMFA is off): `destination` is returned unchanged.
 *
 * Never import this into mh-middleware — see the CI note there.
 */
export default function usePostRegistrationMfa () {
  const userStore = useUserStore()
  const { canEnrolMfa } = useDescopeSession()
  const forceMfa = useForceMfaFlag()

  /**
   * @param {{path: string, query?: object}} destination - where the user ends up, after enrolment when it applies
   * @returns {Promise<{path: string, query: object}>}
   */
  async function resolvePostRegistrationPath (destination) {
    const target = { path: destination?.path || '/dashboard/', query: destination?.query || {} }
    const mustEnrolMfa = forceMfa.value
    const profile = userStore.getUserProfile || {}

    if (!(await isDescopeMfaEnabled()) || !canEnrolMfa.value) return target

    if (mustEnrolMfa) {
      useMfaPostLoginGate().suspendForEnrollment({
        email: profile.email,
        phone: profile.phone_number,
        destination: target,
        allowSkip: false
      })
      return { path: '/dashboard/setup-mfa/', query: {} }
    }

    return { path: '/dashboard/setup-mfa/', query: { next: target.path } }
  }

  return { resolvePostRegistrationPath }
}
